// Vendor
import * as React from 'react';
import { BarSvgProps, ResponsiveBar } from '@nivo/bar';
import { useInView } from 'react-intersection-observer';
import {
  SPOT_BRAND_LIGHTER,
  SPOT_BRAND,
  SPOT_CLEANOUT_LIGHTER,
  SPOT_CLEANOUT,
  SPOT_CORAL_LIGHTER,
  SPOT_CORAL,
  SPOT_YELLOW_LIGHTER,
  SPOT_YELLOW
} from '@thredup/styleguide/src/variables/colors';

// Internal
import { Bar } from './Bar';
import { data } from './data';
import { DEFAULT_CHART_PROPS } from '@src/config/nivo';

const ChartGenerations: React.FC = () => {
  // Hooks
  const [ref, inView] = useInView({
    triggerOnce: true,
    rootMargin: '-200px 0px'
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initialData = data.map((d: any) => ({
    ...d,
    2016: 0,
    2019: 0
  }));

  const chartData = inView ? data : initialData;
  const colors = {
    2016: {
      Boomers: SPOT_CLEANOUT_LIGHTER,
      'Gen X': SPOT_YELLOW_LIGHTER,
      'Gen Z': SPOT_BRAND_LIGHTER,
      Millennials: SPOT_CORAL_LIGHTER
    },
    2019: {
      Boomers: SPOT_CLEANOUT,
      'Gen X': SPOT_YELLOW,
      'Gen Z': SPOT_BRAND,
      Millennials: SPOT_CORAL
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getColors = (bar: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (colors as any)[bar.id][bar.indexValue];
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const LeftAxisTick = (props: any) => {
    const { y, value } = props;

    const subTitles: { [key: string]: string } = {
      'Gen Z': '(<24)',
      Millennials: '(25-37)',
      'Gen X': '(38-55)',
      Boomers: '(>55)'
    };

    return (
      <g transform={`translate(0, ${y - 10})`}>
        <text
          className="text-sm text-gray-7"
          dx={0}
          dominantBaseline="central"
          textAnchor="end"
          transform="translate(-10,0) rotate(0)"
          y={0}
        >
          <tspan fontWeight="bold" x="0">
            {value}
          </tspan>
          <tspan x="0" dy="1.2em">
            {subTitles[value]}
          </tspan>
        </text>
      </g>
    );
  };

  const barProps: BarSvgProps = {
    ...DEFAULT_CHART_PROPS,
    axisBottom: {
      format: (d) => `${d}%`,
      tickPadding: 0,
      tickSize: 0,
      tickValues: [10, 20, 30, 40]
    },
    axisLeft: { tickPadding: 10, tickSize: 0, renderTick: LeftAxisTick },
    barComponent: Bar,
    colors: getColors,
    data: chartData,
    enableGridY: false,
    enableGridX: true,
    enableLabel: true,
    gridXValues: [10, 20, 30, 40],
    groupMode: 'grouped',
    indexBy: 'generation',
    innerPadding: 6,
    isInteractive: true,
    keys: ['2019', '2016'],
    layout: 'horizontal',
    margin: { top: 30, right: 50, bottom: 30, left: 80 },
    maxValue: 40,
    minValue: 0
  };

  return (
    <span ref={ref}>
      <ResponsiveBar {...barProps} />
    </span>
  );
};

export { ChartGenerations };
